import React, { useState, useMemo, useRef } from 'react';
import { FiX, FiDownload, FiUpload, FiCheckCircle, FiAlertTriangle, FiFileText } from 'react-icons/fi';
import toast from 'react-hot-toast';
import API from '../../utils/api';
import { CONFIG, downloadTemplate, parseExcelFile, exportReport } from '../../utils/reconcileExcel';

/*
 * Match the website against a Tally / GST-portal Excel.
 *   type = 'sales'     → Bills (by Bill Number)
 *   type = 'purchases' → Purchases (by Vendor Invoice Number + Vendor)
 * onVerified() is called after bills/purchases are flagged so the parent list can refresh.
 */

const money = (n) =>
  n === null || n === undefined || n === ''
    ? '—'
    : `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d) => {
  if (!d) return '—';
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : String(d);
};

const STATUS = {
  matched:   { label: 'Matched',            bg: '#dcfce7', color: '#15803d' },
  mismatch:  { label: 'Mismatch',           bg: '#fef3c7', color: '#b45309' },
  not_found: { label: 'Not in system',      bg: '#fee2e2', color: '#b91c1c' },
  duplicate: { label: 'Duplicate in Excel', bg: '#ede9fe', color: '#6d28d9' },
  cancelled: { label: 'Cancelled',          bg: '#f3f4f6', color: '#6b7280' },
  invalid:   { label: 'Invalid row',        bg: '#ffe4e6', color: '#be123c' },
};

const FILTERS = [
  { id: 'all',       label: 'All' },
  { id: 'matched',   label: 'Matched' },
  { id: 'mismatch',  label: 'Mismatch' },
  { id: 'not_found', label: 'Not in system' },
  { id: 'other',     label: 'Duplicate / Invalid / Cancelled' },
  { id: 'missing',   label: 'In system, not in Excel' },
];

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)', zIndex: 1000,
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 12px', overflowY: 'auto',
};
const panel = {
  background: '#fff', borderRadius: 12, width: '100%', maxWidth: 1080,
  boxShadow: '0 20px 50px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column',
};
const label = { display: 'block', fontSize: 12, fontWeight: 600, color: '#4b5563', marginBottom: 4 };
const input = { padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, background: '#fff', boxSizing: 'border-box' };
const btn = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
  border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
const btnPrimary = { ...btn, background: 'var(--primary, #2563eb)', borderColor: 'var(--primary, #2563eb)', color: '#fff' };
const th = { textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#6b7280', whiteSpace: 'nowrap', background: '#fafafa', borderBottom: '2px solid #f3f4f6', position: 'sticky', top: 0 };
const td = { padding: '8px 10px', fontSize: 13, verticalAlign: 'top', borderBottom: '1px solid #f3f4f6' };

const diffFields = (r) => {
  const f = new Set();
  (r.diffs || []).forEach((d) => {
    if (/total|amount/i.test(d.field)) f.add('amount');
    else if (/date/i.test(d.field)) f.add('date');
    else f.add('party'); // name / GST / vendor
  });
  return f;
};

// One "Excel vs System" cell
const Pair = ({ excel, system, bad }) => (
  <div style={{ lineHeight: 1.35 }}>
    <div style={{ color: bad ? '#b91c1c' : '#111827', fontWeight: bad ? 700 : 400 }}>{excel || '—'}</div>
    {system !== undefined && (
      <div style={{ fontSize: 12, color: bad ? '#b45309' : '#9ca3af' }}>
        {system ? `System: ${system}` : 'System: —'}
      </div>
    )}
  </div>
);

export default function ReconcileModal({ type, onClose, onVerified }) {
  const cfg = CONFIG[type];
  const fileRef = useRef(null);

  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);       // { rows, headers, sheetName, headerOffset }
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [source, setSource] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);         // { message, missing, foundHeaders }
  const [data, setData] = useState(null);           // API result
  const [filter, setFilter] = useState('all');
  const [limit, setLimit] = useState(200);
  const [verifying, setVerifying] = useState(false);

  const payload = (extra = {}) => ({
    rows: parsed.rows,
    rowOffset: parsed.headerOffset,
    from_date: fromDate || undefined,
    to_date: toDate || undefined,
    ...extra,
  });

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setError(null); setData(null); setParsed(null); setFile(null);
    try {
      setBusy(true);
      const p = await parseExcelFile(f);
      if (!p.rows.length) throw new Error('No data rows found under the header row.');
      if (p.rows.length > 5000) throw new Error(`File has ${p.rows.length} rows. Maximum is 5,000 per upload — please split it.`);
      setFile(f);
      setParsed(p);
      if (!source) setSource(f.name.replace(/\.[^.]+$/, ''));
    } catch (err) {
      setError({ message: err.message || 'Could not read this file.' });
    } finally {
      setBusy(false);
    }
  };

  const runMatch = async () => {
    if (!parsed) return;
    if ((fromDate && !toDate) || (!fromDate && toDate)) {
      toast.error('Choose both From and To dates, or leave both empty'); return;
    }
    setBusy(true); setError(null);
    try {
      const { data: res } = await API.post(`/reconcile/${type}/match`, payload(), { timeout: 120000 });
      setData(res); setFilter('all'); setLimit(200);
    } catch (err) {
      const d = err.response?.data;
      setData(null);
      setError({ message: d?.message || err.message || 'Match failed', missing: d?.missing, foundHeaders: d?.foundHeaders });
    } finally {
      setBusy(false);
    }
  };

  const verifyAll = async () => {
    const n = data?.summary?.verifiable || 0;
    if (!n) return;
    if (!window.confirm(`Mark ${n} fully-matched ${type === 'sales' ? 'bill' : 'purchase'}${n > 1 ? 's' : ''} as verified?`)) return;
    setVerifying(true);
    try {
      const { data: res } = await API.post(`/reconcile/${type}/verify`, payload({ source }), { timeout: 120000 });
      toast.success(res.message || `${res.verified} verified`);
      if (onVerified) onVerified();
      await runMatch();                          // refresh statuses
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not verify');
    } finally {
      setVerifying(false);
    }
  };

  const rowsShown = useMemo(() => {
    if (!data) return [];
    if (filter === 'missing') return [];
    return data.results.filter((r) => {
      if (filter === 'all') return true;
      if (filter === 'other') return ['duplicate', 'invalid', 'cancelled'].includes(r.status);
      return r.status === filter;
    });
  }, [data, filter]);

  const counts = useMemo(() => {
    if (!data) return {};
    const s = data.summary;
    return {
      all: s.total, matched: s.matched, mismatch: s.mismatch, not_found: s.not_found,
      other: s.duplicate + s.invalid + s.cancelled, missing: s.missingFromExcel || 0,
    };
  }, [data]);

  const partyHead = type === 'sales' ? 'Customer / GST' : 'Vendor / GSTIN';
  const numberHead = type === 'sales' ? 'Bill No.' : 'Invoice No.';
  const dateHead = type === 'sales' ? 'Bill Date' : 'Invoice Date';
  const amountHead = type === 'sales' ? 'Grand Total' : 'Amount';

  return (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget && !busy && !verifying) onClose(); }}>
      <div style={panel}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>Match with Excel — {cfg.title}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              Compare a Tally or GST-portal Excel with the website. Nothing changes until you mark matches as verified.
            </div>
          </div>
          <button onClick={onClose} style={{ ...btn, padding: 6 }} aria-label="Close"><FiX size={16} /></button>
        </div>

        {/* Step 1 + 2 */}
        <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, borderBottom: '1px solid #f3f4f6' }}>
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>1. Get the sample file</div>
            <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 8 }}>Columns the file needs:</div>
            <ul style={{ margin: '0 0 10px', paddingLeft: 18, fontSize: 12, color: '#374151', lineHeight: 1.6 }}>
              {cfg.columns.map((c) => (
                <li key={c.name}>
                  <strong>{c.name}</strong>{' '}
                  <span style={{ color: c.required ? '#b91c1c' : '#b45309' }}>
                    {c.required ? '(required)' : '(required if the other is blank)'}
                  </span>
                </li>
              ))}
            </ul>
            <button style={btn} onClick={() => downloadTemplate(type)}><FiDownload size={14} /> Download sample Excel</button>
          </div>

          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>2. Upload and match</div>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onPickFile} style={{ display: 'none' }} />
            <button style={btn} onClick={() => fileRef.current?.click()} disabled={busy}>
              <FiUpload size={14} /> {file ? 'Choose a different file' : 'Choose Excel file'}
            </button>
            {file && parsed && (
              <div style={{ fontSize: 12, color: '#374151', marginTop: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
                <FiFileText size={13} /> {file.name} — {parsed.rows.length} rows (sheet “{parsed.sheetName}”)
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
              <div>
                <label style={label}>Also list records missing from Excel — From</label>
                <input type="date" style={input} value={fromDate} onChange={(e) => setFromDate(e.target.value)} onClick={(e) => e.target.showPicker?.()} />
              </div>
              <div>
                <label style={label}>To</label>
                <input type="date" style={input} value={toDate} onChange={(e) => setToDate(e.target.value)} onClick={(e) => e.target.showPicker?.()} />
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Optional. Leave empty to skip the “in system, not in Excel” list.</div>

            <div style={{ marginTop: 12 }}>
              <label style={label}>Source label (saved on verified records)</label>
              <input style={{ ...input, width: '100%' }} value={source} onChange={(e) => setSource(e.target.value)} placeholder="e.g. Tally Oct-2026" maxLength={150} />
            </div>

            <div style={{ marginTop: 12 }}>
              <button style={{ ...btnPrimary, opacity: !parsed || busy ? 0.6 : 1 }} disabled={!parsed || busy} onClick={runMatch}>
                {busy ? 'Working…' : 'Match now'}
              </button>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ margin: '16px 20px 0', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 12, fontSize: 13, color: '#991b1b' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <FiAlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                {error.message}
                {error.foundHeaders && (
                  <div style={{ marginTop: 6, fontSize: 12, color: '#7f1d1d' }}>
                    Columns found in your file: {error.foundHeaders.filter(Boolean).join(', ') || '(none)'}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {data && (
          <div style={{ padding: '16px 20px 20px' }}>
            {/* Filter chips = summary */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              {FILTERS.map((f) => {
                const active = filter === f.id;
                const warn = (f.id === 'mismatch' || f.id === 'not_found') && counts[f.id] > 0;
                return (
                  <button
                    key={f.id}
                    onClick={() => { setFilter(f.id); setLimit(200); }}
                    style={{
                      ...btn, padding: '6px 12px',
                      background: active ? '#111827' : '#fff', color: active ? '#fff' : (warn ? '#b45309' : '#374151'),
                      borderColor: active ? '#111827' : '#e5e7eb',
                    }}
                  >
                    {f.label} <span style={{ opacity: 0.8 }}>({counts[f.id] ?? 0})</span>
                  </button>
                );
              })}
            </div>

            {data.summary.matched > 0 && data.summary.matched === data.summary.alreadyVerified && (
              <div style={{ fontSize: 12, color: '#15803d', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FiCheckCircle size={14} /> All matched rows are already verified.
              </div>
            )}

            {filter !== 'missing' ? (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'auto', maxHeight: 460 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>Excel row</th>
                      <th style={th}>Result</th>
                      <th style={th}>{numberHead}</th>
                      <th style={th}>{dateHead}</th>
                      <th style={th}>{partyHead}</th>
                      <th style={{ ...th, textAlign: 'right' }}>{amountHead}</th>
                      <th style={th}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rowsShown.length === 0 && (
                      <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#9ca3af', padding: 28 }}>No rows in this group.</td></tr>
                    )}
                    {rowsShown.slice(0, limit).map((r) => {
                      const st = STATUS[r.status] || STATUS.invalid;
                      const bad = diffFields(r);
                      return (
                        <tr key={r.rowNo}>
                          <td style={{ ...td, color: '#6b7280' }}>{r.rowNo}</td>
                          <td style={td}>
                            <span style={{ background: st.bg, color: st.color, padding: '2px 9px', borderRadius: 99, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>{st.label}</span>
                            {r.status === 'matched' && r.alreadyVerified && (
                              <div style={{ fontSize: 11, color: '#15803d', marginTop: 3 }}>✓ already verified</div>
                            )}
                          </td>
                          <td style={{ ...td, fontFamily: 'monospace' }}>
                            <Pair excel={r.excel.number} system={r.system ? (r.system.purchaseNumber ? `${r.system.number} · ${r.system.purchaseNumber}` : r.system.number) : undefined} />
                          </td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>
                            <Pair excel={fmtDate(r.excel.date)} system={r.system ? fmtDate(r.system.date) : undefined} bad={bad.has('date')} />
                          </td>
                          <td style={td}>
                            <Pair
                              excel={[r.excel.party, r.excel.gst].filter(Boolean).join(' · ')}
                              system={r.system ? [r.system.party, r.system.gst].filter(Boolean).join(' · ') : undefined}
                              bad={bad.has('party')}
                            />
                          </td>
                          <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <Pair excel={money(r.excel.amount)} system={r.system ? money(r.system.amount) : undefined} bad={bad.has('amount')} />
                          </td>
                          <td style={{ ...td, fontSize: 12, color: '#6b7280', maxWidth: 260 }}>
                            {r.diffs.map((d) => (
                              <div key={d.field} style={{ color: '#b45309' }}>{d.field} differs</div>
                            ))}
                            {r.notes.map((n, i) => <div key={i}>{n}</div>)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'auto', maxHeight: 460 }}>
                {!fromDate || !toDate ? (
                  <div style={{ padding: 28, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                    Choose a From / To date above and press “Match now” to list records that are in the system but not in your Excel.
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={th}>{numberHead}</th><th style={th}>{dateHead}</th><th style={th}>{partyHead}</th>
                        <th style={{ ...th, textAlign: 'right' }}>{amountHead}</th><th style={th}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.missingFromExcel.length === 0 && (
                        <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: '#15803d', padding: 28 }}>Nothing missing — every record in this date range is in your Excel.</td></tr>
                      )}
                      {data.missingFromExcel.map((m) => (
                        <tr key={m.recordId}>
                          <td style={{ ...td, fontFamily: 'monospace' }}>{m.system.number}{m.system.purchaseNumber ? <div style={{ fontSize: 11, color: '#9ca3af' }}>{m.system.purchaseNumber}</div> : null}</td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(m.system.date)}</td>
                          <td style={td}>{[m.system.party, m.system.gst].filter(Boolean).join(' · ') || '—'}</td>
                          <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{money(m.system.amount)}</td>
                          <td style={{ ...td, fontSize: 12 }}>{m.system.status}{m.system.isVerified ? ' · verified' : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {data.truncated && <div style={{ padding: 10, fontSize: 12, color: '#b45309' }}>Showing the first 2,000 only — narrow the date range.</div>}
              </div>
            )}

            {filter !== 'missing' && rowsShown.length > limit && (
              <div style={{ textAlign: 'center', marginTop: 10 }}>
                <button style={btn} onClick={() => setLimit((l) => l + 300)}>Show more ({rowsShown.length - limit} left)</button>
              </div>
            )}

            {/* Footer actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 16 }}>
              <button style={btn} onClick={() => exportReport(type, data, file?.name || '')}>
                <FiDownload size={14} /> Export report to Excel
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, color: '#6b7280' }}>
                  {data.summary.verifiable > 0
                    ? `${data.summary.verifiable} fully-matched ready to verify`
                    : 'No new fully-matched records to verify'}
                </span>
                <button
                  style={{ ...btnPrimary, background: '#15803d', borderColor: '#15803d', opacity: data.summary.verifiable > 0 && !verifying ? 1 : 0.5 }}
                  disabled={!data.summary.verifiable || verifying}
                  onClick={verifyAll}
                >
                  <FiCheckCircle size={14} /> {verifying ? 'Verifying…' : 'Mark matched as verified'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
